import { webLog } from '../grpc/webLogger';
import {
  isBoltProfileAttributeName,
  isBoltProfileObjectTypeName,
} from '../../lib/boltProfileMapping';

const BOLT_PROFILE_TRACE_FIELD_IDS = [
  'boltModelingChoice',
  'boltBaseFamilyValue',
  'boltSetActiveValue',
  'boltDiaValue',
  'boltQuantityValue',
  'boltLengthValue',
  'boltItemDescriptionValue',
  'boltMaterialValue',
  'boltStandardValue',
] as const;

function stringifyRemoteTrace(payload: Record<string, unknown>): string {
  try {
    return JSON.stringify(payload);
  } catch {
    return '"[Unserializable pcf settings trace]"';
  }
}

export function warnPcfSettingsRemote(
  message: string,
  payload: Record<string, unknown>
): void {
  webLog.warn('pcfSettingsClient', `${message} ${stringifyRemoteTrace(payload)}`);
}

export function errorPcfSettingsRemote(
  message: string,
  payload: Record<string, unknown>
): void {
  webLog.error('pcfSettingsClient', `${message} ${stringifyRemoteTrace(payload)}`);
}

function asDebugRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function getDebugString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

export function summarizeProfileBoltPayloadForDebug(
  dataFlow: Record<string, unknown>
): Record<string, unknown> {
  const advancedSettings = asDebugRecord(dataFlow.advancedSettings);
  const wizard = asDebugRecord(advancedSettings?.wizard);
  const bolt = asDebugRecord(wizard?.bolt);
  const boltFields = asDebugRecord(bolt?.fields);
  const isoParameters = asDebugRecord(dataFlow.isoParameters);
  const isoEntries = Array.isArray(isoParameters?.entries)
    ? (isoParameters.entries as Record<string, unknown>[])
    : [];
  const isoRows = isoEntries
    .filter(
      (entry) =>
        isBoltProfileObjectTypeName(getDebugString(entry.objectType)) &&
        isBoltProfileAttributeName(getDebugString(entry.pcfAttribute))
    )
    .map((entry) => ({
      objectType: getDebugString(entry.objectType),
      pcfAttribute: getDebugString(entry.pcfAttribute),
      revitParameter: getDebugString(entry.revitParameter),
      isoType: getDebugString(entry.isoType),
      order: getDebugString(entry.order),
    }));

  return {
    metadataBolt: bolt
      ? {
          parameterDestination: bolt.parameterDestination ?? null,
          parameterBindingKind: bolt.parameterBindingKind ?? null,
          projectParameterCategories: bolt.projectParameterCategories ?? null,
          fields: boltFields
            ? BOLT_PROFILE_TRACE_FIELD_IDS.reduce<Record<string, string>>(
                (fields, fieldId) => {
                  fields[fieldId] = getDebugString(boltFields[fieldId]);
                  return fields;
                },
                {}
              )
            : null,
        }
      : null,
    isoRows,
    isoRowCount: isoRows.length,
  };
}
